import type { CSSProperties } from 'react';
import { fixButtonLabel, SEVERITY_TONE, type DocumentFix } from './documentFixes';

/**
 * A slim floating fix card that hovers OVER the document next to its snippet. Severity-tinted header, the fix
 * instruction, a preview of the suggested wording, and Apply. Purely an overlay — never part of the saved
 * .docx. Positioning + z-index (click-to-front) are handled by the layer in DocxEditorPane.
 */
export function DocxFixMarginCard({
  fix,
  active,
  onActivate,
  onApply,
  onClose,
}: {
  fix: DocumentFix;
  active: boolean;
  onActivate: (id: string | null) => void;
  onApply: (id: string) => void;
  onClose: (id: string) => void;
}) {
  const tone = SEVERITY_TONE[fix.severity];
  return (
    <div
      onMouseEnter={() => onActivate(fix.id)}
      onMouseLeave={() => onActivate(null)}
      style={{
        width: 252,
        pointerEvents: 'auto',
        background: '#fff',
        borderRadius: 9,
        border: `1px solid ${active ? tone.color : 'rgba(38,34,28,0.14)'}`,
        boxShadow: active ? '0 10px 26px rgba(38,34,28,0.22)' : '0 3px 12px rgba(38,34,28,0.16)',
        overflow: 'hidden',
        font: '12px/1.4 system-ui, sans-serif',
        color: '#2b2620',
        cursor: 'default',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          padding: '7px 8px 7px 10px',
          background: tone.bg,
          boxShadow: `inset 3px 0 0 ${tone.color}`,
        }}
      >
        <strong style={{ fontSize: 12.5, lineHeight: 1.25, flex: 1 }}>{fix.title}</strong>
        <button
          type="button"
          aria-label="Close"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onClose(fix.id);
          }}
          style={{
            flex: 'none',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: '#8a8378',
            fontSize: 16,
            lineHeight: 1,
            padding: '0 2px',
          }}
        >
          ×
        </button>
      </div>
      <div style={{ padding: '8px 10px 10px' }}>
        <p style={{ margin: 0, color: '#5c564d' }}>{fix.action}</p>
        <FixPreview fix={fix} color={tone.color} />
        <div style={{ marginTop: 9 }}>
          {fix.fix ? (
            <button
              type="button"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => onApply(fix.id)}
              style={{
                appearance: 'none',
                border: 'none',
                borderRadius: 7,
                background: tone.color,
                color: '#fff',
                fontWeight: 600,
                fontSize: 12,
                padding: '5px 12px',
                cursor: 'pointer',
              }}
            >
              {fixButtonLabel(fix)}
            </button>
          ) : (
            <span style={{ fontStyle: 'italic', color: '#8a8378', fontSize: 11.5 }}>Needs a manual edit</span>
          )}
        </div>
      </div>
    </div>
  );
}

/** Preview of what the operation will do: the new wording (replace), the added text (insert), or the deleted
 *  text struck through (remove). */
function FixPreview({ fix, color }: { fix: DocumentFix; color: string }) {
  const op = fix.fix;
  if (!op) return null;
  const box: CSSProperties = {
    margin: '8px 0 0',
    padding: '6px 9px',
    borderRadius: 4,
    borderLeft: `2px solid ${color}`,
    background: '#f6f7f8',
  };
  if (op.kind === 'replace') {
    return <div style={{ ...box, fontStyle: 'italic', color: '#2b2620' }}>“{op.text}”</div>;
  }
  if (op.kind === 'insert') {
    return (
      <div style={{ ...box, background: '#eef7f1', color: '#22543d' }}>
        <strong style={{ fontWeight: 700 }}>+ </strong>
        <span style={{ fontStyle: 'italic' }}>“{op.text.trim()}”</span>
      </div>
    );
  }
  // remove
  return (
    <div style={{ ...box, background: '#fcf0ee', color: '#742a2a' }}>
      <span style={{ textDecoration: 'line-through', fontStyle: 'italic' }}>“{fix.anchor?.quote?.trim()}”</span>
    </div>
  );
}
